import { getMsalInstance, loginRequest, databricksRequest } from "../msalConfig";
import { getSessionTimeRemainingMs, isSessionExpired } from "../utils/userUtils";

const APP_REFRESH_INTERVAL_MS = 30 * 60 * 1000;
const EXPIRY_SKEW_MS = 2 * 60 * 1000;

class TokenRefreshService {
  constructor() {
    this.refreshTimer = null;
    this.sessionTimer = null;
    this.isLoggingOut = false;
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.databricksRefreshPromise = null;
    this._visibilityListenerAttached = false;
  }

  parseExpiry(key) {
    const raw = localStorage.getItem(key);
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  }

  isExpiring(expiryMs, skewMs = EXPIRY_SKEW_MS) {
    if (!expiryMs) return true;
    return Date.now() >= expiryMs - skewMs;
  }

  isTokenValid() {
    const appToken = localStorage.getItem("appToken");
    if (!appToken) return false;

    const appExpiry = this.parseExpiry("appTokenExpiry");
    if (!appExpiry) return false;

    return !this.isExpiring(appExpiry);
  }

  getTimeUntilExpiry() {
    const appExpiry = this.parseExpiry("appTokenExpiry");
    if (!appExpiry) return 0;
    return Math.max(0, appExpiry - Date.now());
  }

  clearExpiredTokens() {
    const now = Date.now();

    const appExpiry = this.parseExpiry("appTokenExpiry");
    if (appExpiry && now >= appExpiry) {
      localStorage.removeItem("appToken");
      localStorage.removeItem("appTokenExpiry");
    }

    const dbExpiry = this.parseExpiry("databricksTokenExpiry");
    if (dbExpiry && now >= dbExpiry) {
      localStorage.removeItem("databricksToken");
      localStorage.removeItem("databricksTokenExpiry");
    }

    const legacyExpiry = this.parseExpiry("tokenExpiry");
    if (legacyExpiry && now >= legacyExpiry) {
      localStorage.removeItem("tokenExpiry");
    }
  }

  getAccount(msalInstance = getMsalInstance()) {
    const active = msalInstance.getActiveAccount();
    if (active) return active;

    const all = msalInstance.getAllAccounts?.() || [];
    if (all.length > 0) {
      msalInstance.setActiveAccount(all[0]);
      return all[0];
    }

    return null;
  }

  isInteractionRequired(error) {
    const code = String(error?.errorCode || error?.code || "").toLowerCase();
    const message = String(error?.message || "").toLowerCase();
    return (
      code.includes("interaction_required") ||
      code.includes("consent_required") ||
      code.includes("login_required") ||
      code.includes("no_account") ||
      message.includes("interaction_required") ||
      message.includes("consent")
    );
  }

  storeTokens(appToken, databricksToken, expiresOn = null, perTokenExpiry = {}) {
    try {
      const { appExpiresOn = null, databricksExpiresOn = null } = perTokenExpiry || {};

      if (appToken) {
        localStorage.setItem("appToken", appToken);
      }
      if (appExpiresOn) {
        localStorage.setItem("appTokenExpiry", appExpiresOn.toString());
      }

      if (databricksToken) {
        localStorage.setItem("databricksToken", databricksToken);
      }
      if (databricksExpiresOn) {
        localStorage.setItem("databricksTokenExpiry", databricksExpiresOn.toString());
      }

      const earliestKnownExpiry =
        [appExpiresOn, databricksExpiresOn, expiresOn]
          .filter(Boolean)
          .sort((a, b) => a - b)[0] || null;

      if (earliestKnownExpiry) {
        localStorage.setItem("tokenExpiry", earliestKnownExpiry.toString());
      } else if (appToken || databricksToken) {
        localStorage.setItem("tokenExpiry", String(Date.now() + 60 * 60 * 1000));
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  async refreshAppToken({ forceRefresh = true } = {}) {
    const msalInstance = getMsalInstance();
    const account = this.getAccount(msalInstance);
    if (!account) return null;

    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
      forceRefresh,
    });

    const appToken = response.accessToken;
    const appExpiresOn = response.expiresOn ? response.expiresOn.getTime() : null;

    if (appToken) {
      this.storeTokens(appToken, null, appExpiresOn, { appExpiresOn });
      return { appToken, appExpiresOn };
    }

    return null;
  }

  /**
   * Existing login/root hook calls this method. Keep it app-token only.
   * Do NOT acquire Databricks token here; otherwise localhost:3000/root gets stuck
   * when Databricks consent is missing.
   */
  async refreshTokens() {
    if (this.isRefreshing && this.refreshPromise) return this.refreshPromise;

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        return await this.refreshAppToken({ forceRefresh: true });
      } catch (error) {
        return null;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Use this only before query / thinking / dashboard APIs.
   * It first tries silent MSAL, then popup if consent/interaction is required.
   * No PAT fallback and no full-page redirect.
   */
  async ensureDatabricksToken({ forceRefresh = false, interactive = true } = {}) {
    this.clearExpiredTokens();

    const storedToken = localStorage.getItem("databricksToken");
    const storedExpiry = this.parseExpiry("databricksTokenExpiry");
    if (!forceRefresh && storedToken && !this.isExpiring(storedExpiry)) {
      return storedToken;
    }

    if (this.databricksRefreshPromise) return this.databricksRefreshPromise;

    this.databricksRefreshPromise = (async () => {
      const msalInstance = getMsalInstance();
      const account = this.getAccount(msalInstance);
      if (!account) {
        throw new Error("No active Microsoft account found. Please sign in again.");
      }

      const request = {
        ...databricksRequest,
        account,
        forceRefresh,
      };

      try {
        const response = await msalInstance.acquireTokenSilent(request);
        const token = response?.accessToken || null;
        const expiresOn = response?.expiresOn ? response.expiresOn.getTime() : null;
        if (!token) throw new Error("MSAL did not return a Databricks token.");
        this.storeTokens(null, token, expiresOn, { databricksExpiresOn: expiresOn });
        return token;
      } catch (silentError) {
        if (!interactive || !this.isInteractionRequired(silentError)) {
          localStorage.removeItem("databricksToken");
          localStorage.removeItem("databricksTokenExpiry");
          throw silentError;
        }

        const popupResponse = await msalInstance.acquireTokenPopup({
          ...databricksRequest,
          account,
          prompt: "select_account",
        });
        const token = popupResponse?.accessToken || null;
        const expiresOn = popupResponse?.expiresOn ? popupResponse.expiresOn.getTime() : null;
        if (!token) throw new Error("MSAL popup did not return a Databricks token.");
        this.storeTokens(null, token, expiresOn, { databricksExpiresOn: expiresOn });
        return token;
      }
    })().finally(() => {
      this.databricksRefreshPromise = null;
    });

    return this.databricksRefreshPromise;
  }

  scheduleNextRefresh() {
    this.stopAutoRefresh();
    this.refreshTimer = setInterval(() => {
      this.refreshTokens();
    }, APP_REFRESH_INTERVAL_MS);
  }

  stopSessionTimeout() {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }
  }

  scheduleSessionTimeout() {
    this.stopSessionTimeout();

    if (isSessionExpired()) {
      this.enforceSessionLogout();
      return;
    }

    const remainingMs = getSessionTimeRemainingMs();
    const maxTimeout = 2_147_483_647;
    const delay = Math.min(remainingMs, maxTimeout);

    this.sessionTimer = setTimeout(() => {
      this.enforceSessionLogout();
    }, delay);
  }

  async enforceSessionLogout() {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;
    const { performAppLogout } = await import("../utils/authLogout");
    await performAppLogout();
  }

  startAutoRefresh() {
    this.clearExpiredTokens();

    if (isSessionExpired()) {
      this.enforceSessionLogout();
      return;
    }

    this.scheduleNextRefresh();
    this.scheduleSessionTimeout();
    this.setupVisibilityListener();
  }

  setupVisibilityListener() {
    if (this._visibilityListenerAttached) return;
    this._visibilityListenerAttached = true;

    document.addEventListener("visibilitychange", async () => {
      if (document.hidden) return;

      if (isSessionExpired()) {
        await this.enforceSessionLogout();
        return;
      }

      this.clearExpiredTokens();
      const appExpiry = this.parseExpiry("appTokenExpiry");
      if (this.isExpiring(appExpiry, 30 * 60 * 1000)) {
        await this.refreshTokens();
      }
    });
  }

  stopAutoRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.stopSessionTimeout();
  }

  logout() {
    this.stopAutoRefresh();
    localStorage.removeItem("appToken");
    localStorage.removeItem("appTokenExpiry");
    localStorage.removeItem("databricksToken");
    localStorage.removeItem("databricksTokenExpiry");
    localStorage.removeItem("tokenExpiry");
  }
}

export const tokenRefreshService = new TokenRefreshService();
export default tokenRefreshService;
